/**
 * Rest Client class
 * Responsible for consuming Api endpoints
 *
 * * getRequest
 * * postRequest
 * * putRequest
 * * deleteRequest
 */
import React from 'react'
import axios from 'axios'
import Configuration from '../Api/Configuration'
import keycloak from '../keycloak'
import '../axios'
import { handleNotificationAlert, log } from '../../utils/helpers'
import { FormattedMessage } from 'react-intl'
import CookiesStorageService from '../CookiesStorageService'

class RestClient {
  constructor () {
    this.config = new Configuration()
  }

  static setFormikErrors = (errorObject, setFormikError) => {
    let errors = Object.keys(errorObject)
    if (!!errors?.length) {
      errors.map(errorItem => setFormikError(errorItem, errorObject[errorItem].join('\r\n')))
    }
  }

  static getRequest = async (url, config = {}, defaultErrorMessage, returnError, noNotification, default404ErrorMessage, default401ErrorMessage) => {
    let configurations = {
      ...config,
      headers: {
        'Authorization': `Bearer ${keycloak?.token ?? ''}`,
        trn: CookiesStorageService.getTaxpayerTRN(),
        ...(config?.headers || {})
      }
    }
    return await axios.get(url, configurations)
      .then(response => {return response })
      .catch((error) => {
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          if (!noNotification) {
            let message = (error.response.status === 404 && !!default404ErrorMessage)
              ? default404ErrorMessage
              : (([400, 401, 403].includes(error.response.status) && !!default401ErrorMessage)
                ? default401ErrorMessage
                : (defaultErrorMessage || <FormattedMessage id='BasicInfo.FormErrorAlert'/>))
            let messagesList = []
            if (!!error.response && error.response.data) {
              if ((!default401ErrorMessage || error.response.data?.error?.code === 'ValidationError') && [400,404].includes(error.response.status) && error.response.data?.error?.details?.length) {
                error.response.data?.error?.details.forEach(msg => {
                  messagesList.push(<>
                    <strong>{`${!!msg.code ? msg.code : error.response.data?.error?.code || ''} ${!!msg.target ? `(${msg.target})` : ''}`}:</strong> {msg.message}</>)
                })
              } else if (!([400, 401, 403].includes(error.response.status) && !!default401ErrorMessage)) {
                if (error.response.data.message) {
                  message = error.response.data.message
                } else if (error.response.data.error) {
                  message = error.response.data.error
                } else if (error.response.data?.status?.message) {
                  message = error.response.data.status.message
                } else if (error.response.data?.status?.error) {
                  message = error.response.data.status.error
                }
                if (error.response.status !== 422) {
                  message = <><strong>{error.response.status}:</strong> {message}</>
                }
              }
            }
            if (!!messagesList?.length) {
              messagesList.forEach(msg => {
                handleNotificationAlert({
                  type: 'defence',
                  message: msg,
                  color: 'error'
                }, 'error')
              })
            } else {
              handleNotificationAlert({
                type: 'defence',
                message: message,
                color: 'error'
              }, 'error')
            }
          }
          log('getRequest data:', error.response.data)
          log('getRequest status:', error.response.status)
          log('getRequest headers:', error.response.headers)
        } else if (error.request) {
          // The request was made but no response was received
          // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
          // http.ClientRequest in node.js
          log('getRequest request:', error.request)
          log('getRequest request:', error.message)
          if (!noNotification) {
            handleNotificationAlert({
              type: 'defence',
              message: <FormattedMessage id='Basic.RequestErrorAlert'/>,
              color: 'error'
            }, 'error')
          }
        } else {
          // Something happened in setting up the request that triggered an Error
          log('getRequest Error Message:', error.message)
        }
        log('getRequest config:', error.config)
        if (!!returnError) {
          return error
        }
      })
  }

  static getPostRequest = async (url, data = {}, defaultErrorMessage, returnError, noNotification, default404ErrorMessage, default401ErrorMessage) => {
    let configurations = {
      headers: {
        'Authorization': `Bearer ${keycloak?.token ?? ''}`,
        trn: CookiesStorageService.getTaxpayerTRN(),
      }
    }
    return await axios.post(url, data, configurations)
      .then(response => {return response })
      .catch((error) => {
        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          if (!noNotification) {
            let message = (error.response.status === 404 && !!default404ErrorMessage)
              ? default404ErrorMessage
              : (([400, 401, 403].includes(error.response.status) && !!default401ErrorMessage)
                ? default401ErrorMessage
                : (defaultErrorMessage || <FormattedMessage id='BasicInfo.FormErrorAlert'/>))
            let messagesList = []
            if (!!error.response && error.response.data) {
              if (!default401ErrorMessage && [400].includes(error.response.status) && error.response.data?.error?.details?.length) {
                error.response.data?.error?.details.forEach(msg => {
                  messagesList.push(<>
                    <strong>{`${!!msg.code ? msg.code : error.response.data?.error?.code || ''} ${!!msg.target ? `(${msg.target})` : ''}`}:</strong> {msg.message}</>)
                })
              } else if (!([400, 401, 403].includes(error.response.status) && !!default401ErrorMessage)) {
                if (error.response.data.message) {
                  message = error.response.data.message
                } else if (error.response.data.error) {
                  message = error.response.data.error
                } else if (error.response.data?.status?.message) {
                  message = error.response.data.status.message
                } else if (error.response.data?.status?.error) {
                  message = error.response.data.status.error
                }
                if (error.response.status !== 422) {
                  message = <><strong>{error.response.status}:</strong> {message}</>
                }
              }
            }
            if (!!messagesList?.length) {
              messagesList.forEach(msg => {
                handleNotificationAlert({
                  type: 'defence',
                  message: msg,
                  color: 'error'
                }, 'error')
              })
            } else {
              handleNotificationAlert({
                type: 'defence',
                message: message,
                color: 'error'
              }, 'error')
            }
          }
          log('getRequest data:', error.response.data)
          log('getRequest status:', error.response.status)
          log('getRequest headers:', error.response.headers)
        } else if (error.request) {
          // The request was made but no response was received
          // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
          // http.ClientRequest in node.js
          log('getRequest request:', error.request)
          log('getRequest request:', error.message)
          if (!noNotification) {
            handleNotificationAlert({
              type: 'defence',
              message: <FormattedMessage id='Basic.RequestErrorAlert'/>,
              color: 'error'
            }, 'error')
          }
        } else {
          // Something happened in setting up the request that triggered an Error
          log('getRequest Error Message:', error.message)
        }
        log('getRequest config:', error.config)
        if (!!returnError) {
          return error
        }
      })
  }

  static postRequest (url, data, config = {}, setFormLoading, setFailure, FormikActions, defaultErrorMessage = '', default401ErrorMessage = '') {
    let configurations = {
      ...config,
      headers: {
        'Authorization': `Bearer ${keycloak?.token ?? ''}`,
        trn: CookiesStorageService.getTaxpayerTRN(),
        ...(config?.headers || {})
      }
    }
    return axios.post(url, data, configurations)
      .then(response => { return response })
      .catch((error) => {
        // eslint-disable-next-line no-unused-expressions
        if (!!FormikActions) {
          try {
            FormikActions.setSubmitting(false)
          } catch (e) {
            FormikActions.isSubmitting = false
          }
        }
        // eslint-disable-next-line no-unused-expressions
        !!setFormLoading ? setFormLoading(false) : ''
        // eslint-disable-next-line no-unused-expressions
        if (!!setFailure) {
          setFailure(true)
        }

        let message = ([400, 401, 403].includes(error?.response?.status) && !!default401ErrorMessage)
          ? default401ErrorMessage
          : (defaultErrorMessage || <FormattedMessage id='BasicInfo.FormErrorAlert'/>)
        let messagesList = []
        if (!!error.response && error.response.data) {
          if ((!default401ErrorMessage || error.response.data?.error?.code === 'ValidationError') && error.response.status === 400 && error.response.data?.error?.details?.length) {
            error.response.data?.error?.details.forEach(msg => {
              messagesList.push(<>
                <strong>{`${!!msg.code ? msg.code : ''} ${!!msg.target ? `(${msg.target})` : ''}`}:</strong> {msg.message}</>)
            })
          } else if (!([400, 401, 403].includes(error?.response?.status) && !!default401ErrorMessage)) {
            if (error?.response?.data?.message) {
              message = error.response.data.message
            } else if (error.response.data.error) {
              message = error.response.data.error
            } else if (error.response.data?.status?.message) {
              message = error.response.data.status.message
            } else if (error.response.data?.status?.error) {
              message = error.response.data.status.error
            } else if (error.response.data?.title) {
              message = error.response.data?.title
            } else if (error.response.data?.responseMessage) {
              message = error.response.data?.responseMessage
            }
            if (error?.response?.status !== 422) {
              message = <>{message}</>
            }
          }
        }
        if (!!messagesList?.length) {
          messagesList.forEach(msg => {
            handleNotificationAlert({
              type: 'defence',
              message: msg,
              color: 'error'
            }, 'error')
          })
        } else {
          handleNotificationAlert({
            type: 'defence',
            message: message,
            color: 'error'
          }, 'error')
        }

        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          log('postRequest data:', error.response.data)
          log('postRequest status:', error.response.status)
          log('postRequest headers:', error.response.headers)
        } else if (error.request) {
          // The request was made but no response was received
          // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
          // http.ClientRequest in node.js
          log('postRequest request:', error.request)
        } else {
          // Something happened in setting up the request that triggered an Error
          log('postRequest Error Message:', error.message)
        }
        log('postRequest config:', error.config)
      })
  }

  static putRequest (url, data, config = {}, setFormLoading, setFailure, FormikActions, defaultErrorMessage = '', default401ErrorMessage = '') {
    let configurations = {
      ...config,
      headers: {
        'Authorization': `Bearer ${keycloak?.token ?? ''}`,
        trn: CookiesStorageService.getTaxpayerTRN(),
        ...(config?.headers || {})
      }
    }
    return axios.put(url, data, configurations)
      .then(response => {return response })
      .catch((error) => {
          // eslint-disable-next-line no-unused-expressions
          !!FormikActions ? FormikActions.setSubmitting(false) : ''
          // eslint-disable-next-line no-unused-expressions
          !!setFormLoading ? setFormLoading(false) : ''
          // eslint-disable-next-line no-unused-expressions
          !!setFailure ? setFailure(true) : ''

          let message = ([400, 401, 403].includes(error.response.status) && !!default401ErrorMessage)
            ? default401ErrorMessage
            : (defaultErrorMessage || <FormattedMessage id='BasicInfo.FormErrorAlert'/>)
          let messagesList = []
          if (!!error.response && error.response.data) {
            if ((!default401ErrorMessage || error.response.data?.error?.code === 'ValidationError') && error.response.status !== 200 && error.response.data?.error?.details?.length) {
              error.response.data?.error?.details.forEach(msg => {
                messagesList.push(<>
                  <strong>{`${!!msg.code ? msg.code : ''} ${!!msg.target ? `(${msg.target})` : ''}`}:</strong> {msg.message}</>)
              })
            } else if (!([400, 401, 403].includes(error.response.status) && !!default401ErrorMessage)) {
              if (error.response.data.message) {
                message = error.response.data.message
              } else if (error.response.data.error) {
                message = error.response.data.error
              } else if (error.response.data?.status?.message) {
                message = error.response.data.status.message
              } else if (error.response.data?.status?.error) {
                message = error.response.data.status.error
              }
              if (error.response.status !== 422) {
                message = <><strong>{error.response.status}:</strong> {message}</>
              }
            }
          }
          if (!!messagesList?.length) {
            messagesList.forEach(msg => {
              handleNotificationAlert({
                type: 'defence',
                message: msg,
                color: 'error'
              }, 'error')
            })
          } else {
            handleNotificationAlert({
              type: 'defence',
              message: message,
              color: 'error'
            }, 'error')
          }
          if (error.response) {
            // The request was made and the server responded with a status code
            // that falls out of the range of 2xx
            log('putRequest data:', error.response.data)
            log('putRequest status:', error.response.status)
            log('putRequest headers:', error.response.headers)
          } else if (error.request) {
            // The request was made but no response was received
            // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
            // http.ClientRequest in node.js
            log('putRequest request:', error.request)
          } else {
            // Something happened in setting up the request that triggered an Error
            log('putRequest Error Message:', error.message)
          }
          log('putRequest config:', error.config)
        }
      )
  }

  static deleteRequest (url, config = {}, setFormLoading, setFailure, FormikActions, defaultErrorMessage = '', default401ErrorMessage = '') {
    let configurations = {
      ...config,
      headers: {
        'Authorization': `Bearer ${keycloak?.token ?? ''}`,
        trn: CookiesStorageService.getTaxpayerTRN(),
        ...(config?.headers || {})
      }
    }
    return axios.delete(url, configurations)
      .then(response => {return response })
      .catch((error) => {
        // eslint-disable-next-line no-unused-expressions
        !!FormikActions ? FormikActions.setSubmitting(false) : ''
        // eslint-disable-next-line no-unused-expressions
        !!setFormLoading ? setFormLoading(false) : ''
        // eslint-disable-next-line no-unused-expressions
        !!setFailure ? setFailure(true) : ''

        let message = ([400, 401, 403].includes(error.response.status) && !!default401ErrorMessage)
          ? default401ErrorMessage
          : (defaultErrorMessage || <FormattedMessage id='BasicInfo.FormErrorAlert'/>)
        if ((!!error.response && error.response.data) && (!([400, 401, 403].includes(error.response.status) && !!default401ErrorMessage))) {
          if (error.response.data.message) {
            message = error.response.data.message
          } else if (error.response.data.error) {
            message = error.response.data.error
          } else if (error.response.data?.status?.message) {
            message = error.response.data.status.message
          } else if (error.response.data?.status?.error) {
            message = error.response.data.status.error
          }
          if (error.response.status !== 422) {
            message = <><strong>{error.response.status}:</strong> {message}</>
          }
        }
        handleNotificationAlert({
          type: 'defence',
          message: message,
          color: 'error'
        }, 'error')

        if (error.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          log('deleteRequest data:', error.response.data)
          log('deleteRequest status:', error.response.status)
          log('deleteRequest headers:', error.response.headers)
        } else if (error.request) {
          // The request was made but no response was received
          // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
          // http.ClientRequest in node.js
          log('deleteRequest request:', error.request)
        } else {
          // Something happened in setting up the request that triggered an Error
          log('deleteRequest Error Message:', error.message)
        }
        log('deleteRequest config:', error.config)
      })
  }
}

export default RestClient
